// src/context/UserHistoryContext.js
//
// Single source of truth for the logged-in user's watch/like/dislike history.
// Fetched once per user instead of once per MovieCard, eliminating the N+1
// `/api/users/userhistory?movie_id=X` request-per-card pattern.

import React, { createContext, useState, useEffect, useCallback, useMemo, useContext } from 'react';
import axios from 'axios';
import AuthContext from './AuthContext';

const UserHistoryContext = createContext();

const EMPTY_ACTION = { watched: false, liked: false, disliked: false };

export const UserHistoryProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [historyMap, setHistoryMap] = useState(new Map()); // movieId -> {watched, liked, disliked}
  // Starts true so consumers can wait for the first load to finish before
  // acting on an empty map (e.g. HomePage's watched-movie filter).
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    if (!user?.id) {
      setHistoryMap(new Map());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get('/api/users/userhistory', {
        params: { user_id: user.id },
      });
      const map = new Map();
      data.forEach((action) => {
        map.set(action.movie_id, {
          watched: !!action.watched,
          liked: !!action.liked,
          disliked: !!action.disliked,
        });
      });
      setHistoryMap(map);
    } catch (error) {
      console.error('Error fetching user history:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const getAction = useCallback(
    (movieId) => historyMap.get(movieId) || EMPTY_ACTION,
    [historyMap]
  );

  // Persists one action (watched/liked/disliked) and updates the shared map
  // from the server's response, so every MovieCard showing this movie
  // reflects the change immediately without refetching.
  const updateAction = useCallback(
    async (movieId, patch) => {
      if (!user?.id) return;
      const current = historyMap.get(movieId) || EMPTY_ACTION;
      const payload = {
        user_id: user.id,
        movie_id: movieId,
        watched: patch.watched !== undefined ? patch.watched : current.watched,
        liked: patch.liked !== undefined ? patch.liked : current.liked,
        disliked: patch.disliked !== undefined ? patch.disliked : current.disliked,
      };
      const { data } = await axios.post('/api/users/userhistory', payload);
      setHistoryMap((prev) => {
        const next = new Map(prev);
        next.set(movieId, {
          watched: !!data.watched,
          liked: !!data.liked,
          disliked: !!data.disliked,
        });
        return next;
      });
      return data;
    },
    [user, historyMap]
  );

  const watchedIds = useMemo(
    () => [...historyMap.entries()].filter(([, v]) => v.watched).map(([id]) => id),
    [historyMap]
  );
  const likedIds = useMemo(
    () => [...historyMap.entries()].filter(([, v]) => v.liked).map(([id]) => id),
    [historyMap]
  );
  const dislikedIds = useMemo(
    () => [...historyMap.entries()].filter(([, v]) => v.disliked).map(([id]) => id),
    [historyMap]
  );

  return (
    <UserHistoryContext.Provider
      value={{ getAction, updateAction, watchedIds, likedIds, dislikedIds, loading, refresh: fetchHistory }}
    >
      {children}
    </UserHistoryContext.Provider>
  );
};

export default UserHistoryContext;
